// "Edit flight details" modal of a charter booking: flight ID, status, wire transfer received.
import type { FormInstance } from 'antd';
import { Button, Form, Input, Modal, Popconfirm, Select, Typography } from 'antd';
import { LIMITS } from '../../data/limits';
import dayjs from 'dayjs';

import type { CharterBookingDetail, CharterBookingStatus } from '../../api/types';
import { CHARTER_BOOKING_STATUS_LABELS } from '../../data/statuses';
import { gbp } from '../../data/format';
import type { CharterBookingUpdate } from './charterBookingUpdate';

const { Text } = Typography;

const STATUSES: CharterBookingStatus[] = ['pre_approved', 'confirmed', 'cancelled'];

export function FlightDetailsModal({
  b,
  rev,
  flightForm,
  flightOpen,
  setFlightOpen,
  submitFlight,
  submitGuard,
  saveMut,
}: {
  b: CharterBookingDetail;
  rev: CharterBookingDetail['revenue'];
  flightForm: FormInstance;
  flightOpen: boolean;
  setFlightOpen: (open: boolean) => void;
  submitFlight: () => void;
  submitGuard: { busy: boolean };
  saveMut: { isPending: boolean; mutate: (body: CharterBookingUpdate) => void };
}) {
  return (
    <Modal
      title="Edit flight details"
      open={flightOpen}
      onCancel={() => setFlightOpen(false)}
      onOk={submitFlight}
      confirmLoading={submitGuard.busy || saveMut.isPending}
      okText="Save"
      destroyOnClose
    >
      <Form form={flightForm} layout="vertical">
        <Form.Item name="flightNumber" label="Flight ID">
          <Input placeholder="e.g. SJ1234" maxLength={LIMITS.flightNumber} />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Required' }]}>
          <Select
            options={STATUSES.map((s) => ({
              value: s,
              label: CHARTER_BOOKING_STATUS_LABELS[s],
            }))}
          />
        </Form.Item>
        {/* SHAR-130 — a wire booking is only settled once someone confirms the
            transfer arrived; that is a separate fact from the status. */}
        {b.paymentMethod === 'wire_transfer' ? (
          <Form.Item label="Wire transfer">
            {b.wirePaidAt ? (
              <Text type="success">
                Received {dayjs(b.wirePaidAt).format('DD/MM/YYYY HH:mm')}
              </Text>
            ) : (
              // PO, 29 Sep — asks first: the admin cannot undo it, and it is the
              // fact that clears "Wire transfer required".
              <Popconfirm
                title="Mark the payment as received?"
                description={`Confirm only once the transfer of ${gbp(rev.remainingGbp)} has reached ShareJet. This cannot be undone.`}
                okText="Mark as received"
                cancelText="Cancel"
                onConfirm={() => saveMut.mutate({ wirePaid: true })}
              >
                <Button loading={saveMut.isPending}>Mark payment as received</Button>
              </Popconfirm>
            )}
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
}
